import React from 'react';
import {
  Users,
  Eye,
  MessageCircle,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  MoreVertical,
  Activity,
  UserPlus,
  ShieldAlert
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import PostCreator from '../news-feed/components/PostCreator';
import { useAuth } from '../../contexts/AuthContext';

const data = [
  { name: 'Mon', users: 4000, posts: 2400 },
  { name: 'Tue', users: 3000, posts: 1398 },
  { name: 'Wed', users: 2000, posts: 9800 },
  { name: 'Thu', users: 2780, posts: 3908 },
  { name: 'Fri', users: 1890, posts: 4800 },
  { name: 'Sat', users: 2390, posts: 3800 },
  { name: 'Sun', users: 3490, posts: 4300 },
];

const StatCard = ({ title, value, change, isPositive, icon: Icon, color, glow, delay = 0 }) => (
  <div
    className="admin-card p-6 rounded-3xl animate-in fade-in slide-in-from-bottom-4 duration-700"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex justify-between items-start mb-4">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${glow}33, ${glow}11)`, border: `1px solid ${glow}44` }}
      >
        <Icon size={24} style={{ color: glow }} />
      </div>
      <button className="text-white/20 hover:text-white/50 transition-colors">
        <MoreVertical size={20} />
      </button>
    </div>
    <div>
      <p className="text-white/40 text-sm font-medium mb-1">{title}</p>
      <h3 className="text-3xl font-bold text-white stat-glow">{value}</h3>
      <div className="flex items-center gap-1 mt-2">
        <span className={`flex items-center text-xs font-bold ${isPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
          {isPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {change}%
        </span>
        <span className="text-white/30 text-xs">vs last week</span>
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const { user } = useAuth();
  
  // Format user for PostCreator
  const currentUser = user ? {
    id: user.id,
    name: user.user_metadata?.full_name || user.email,
    username: user.user_metadata?.username || user.email.split('@')[0],
    avatar: user.user_metadata?.avatar_url || "https://images.unsplash.com/photo-1724128195747-dd25cba7860f",
    avatarAlt: 'Admin avatar'
  } : null;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Admin Dashboard</h1>
          <p className="text-white/40 mt-1">Welcome back. Here's what's happening today.</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 glass-panel text-white/70 border border-white/10 rounded-xl font-medium hover:border-white/30 transition-all text-sm">
            Download Report
          </button>
          <button className="px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-xl font-medium shadow-lg shadow-violet-900/40 transition-all text-sm">
            Manage System
          </button>
        </div>
      </div>

      {/* Admin Post Creator */}
      {currentUser && (
        <div className="glass-panel p-4 rounded-3xl border border-white/10 shadow-sm relative z-10">
          <h3 className="text-lg font-bold text-white mb-4">Post Global Announcement</h3>
          <PostCreator user={currentUser} />
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Total Users"   value="124.5k" change="12.5" isPositive={true}  icon={Users}       glow="#a78bfa" delay={0}   />
        <StatCard title="Daily Active"  value="42.8k"  change="8.2"  isPositive={true}  icon={Activity}    glow="#10b981" delay={60}  />
        <StatCard title="Total Posts"   value="856k"   change="2.4"  isPositive={false} icon={MessageCircle} glow="#f59e0b" delay={120} />
        <StatCard title="Reports"       value="124"    change="15.8" isPositive={false} icon={ShieldAlert}  glow="#ef4444" delay={180} />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="admin-card p-6 rounded-3xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-white">User Growth</h3>
            <select className="bg-slate-50 dark:bg-slate-800 border-none rounded-lg text-sm font-medium py-1 pl-2 pr-8 focus:ring-0 outline-none dark:text-slate-300">
              <option>Last 7 days</option>
              <option>Last 30 days</option>
            </select>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" className="dark:stroke-slate-800" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    borderRadius: '12px',
                    border: 'none',
                    color: '#fff'
                  }}
                  itemStyle={{ color: '#fff' }}
                />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke="#4f46e5"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorUsers)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="admin-card p-6 rounded-3xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-white">Post Engagement</h3>
            <select className="bg-white/5 border border-white/10 rounded-lg text-sm font-medium py-1 pl-2 pr-6 focus:ring-0 outline-none text-white/60">
              <option>Last 7 days</option>
              <option>Last 30 days</option>
            </select>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" className="dark:stroke-slate-800" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(226, 232, 240, 0.4)' }}
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    borderRadius: '12px',
                    border: 'none',
                    color: '#fff'
                  }}
                />
                <Bar
                  dataKey="posts"
                  fill="#10b981"
                  radius={[6, 6, 0, 0]}
                  barSize={30}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="admin-card rounded-3xl overflow-hidden">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Recent Moderation Actions</h3>
          <button className="text-violet-400 text-sm font-semibold hover:text-violet-300 transition-colors">View All</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white/5">
                <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider">Moderator</th>
                <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider">Action</th>
                <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider">Target</th>
                <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {[1, 2, 3, 4, 5].map((item) => (
                <tr key={item} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/10 overflow-hidden">
                        <img src={`https://i.pravatar.cc/150?u=${item}`} alt="Moderator" />
                      </div>
                      <span className="text-sm font-medium text-white/70">Admin {item}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/50">
                    Deleted Post
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-violet-400 font-medium">
                    #post_9482
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/40">
                    2 hours ago
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                      Completed
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
